'use client';
import * as _ from 'es-toolkit';
import { useSession } from '@/lib/auth-client';
import { useUserReactions, useEngagementSummary } from '@/lib/hooks';
import { getReactionEmoji } from '@/lib/config/reactions';
import { useReactionDialog } from '@/lib/context/ReactionDialogContext';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

interface CompactReactionButtonProps {
  objectId: string;
  objectType: 'item' | 'entity' | 'initiative';
  variant?: 'default' | 'compact';
  className?: string;
  title?: string;
  details?: React.ReactNode;
  // Optional pre-fetched data to avoid individual API calls
  userReactions?: Array<{ id: string; type: string; reactionType: string; createdAt: string }>;
  engagementSummary?: {
    reactions: {
      total: number;
      by_type: Record<string, { count: number; last_at: string }>;
    };
    votes: {
      up: { count: number; last_at: string };
      down: { count: number; last_at: string };
      interested?: { count: number; last_at: string };
    };
  };
}

export function CompactReactionButton({
  objectId,
  objectType,
  variant = 'default',
  className,
  title,
  details,
  userReactions: providedUserReactions,
  engagementSummary: providedEngagementSummary,
}: CompactReactionButtonProps) {
  const { data: session } = useSession();
  const currentUserId = session?.user?.id?.toString();
  const { openDialog } = useReactionDialog();

  // Only fetch if not provided (undefined means not provided, vs empty array/object which means no data)
  const shouldFetchUserReactions = providedUserReactions === undefined;
  const shouldFetchEngagement = providedEngagementSummary === undefined;

  const { data: fetchedUserReactions = [] } = useUserReactions(
    currentUserId || '',
    objectId,
    objectType,
    { enabled: shouldFetchUserReactions && !!currentUserId }
  );
  const { data: fetchedEngagementSummary } = useEngagementSummary(objectId, objectType, {
    enabled: shouldFetchEngagement,
  });

  // Use provided data if available, otherwise use fetched data
  const [userReactions, setUserReactions] = useState(providedUserReactions ?? fetchedUserReactions);
  const [engagementSummary, setEngagementSummary] = useState(
    providedEngagementSummary ?? fetchedEngagementSummary
  );

  const isCompact = variant === 'compact';

  // Handle both formats: direct engagement data from API or wrapped from hook
  const engagementData =
    'engagement' in (engagementSummary || {})
      ? (engagementSummary as any).engagement
      : engagementSummary;

  const totalReactions = engagementData?.reactions?.total || 0;
  const hasUserReaction = userReactions.length > 0;

  // Extract reaction type from the new structure
  const firstReactionType = hasUserReaction
    ? typeof userReactions[0] === 'string'
      ? userReactions[0]
      : userReactions[0].reactionType
    : null;
  const userReactionEmoji = firstReactionType
    ? getReactionEmoji(objectType, firstReactionType)
    : null;

  // Update local state when provided props change (e.g., from query cache updates)
  useEffect(() => {
    if (providedUserReactions !== undefined) {
      setUserReactions(providedUserReactions);
    }
  }, [providedUserReactions]);

  useEffect(() => {
    if (providedEngagementSummary !== undefined) {
      setEngagementSummary(providedEngagementSummary);
    }
  }, [providedEngagementSummary]);

  // Update local state when fetched data changes
  useEffect(() => {
    if (shouldFetchEngagement && fetchedEngagementSummary) {
      setEngagementSummary(fetchedEngagementSummary);
    }
  }, [shouldFetchEngagement, fetchedEngagementSummary]);

  useEffect(() => {
    if (shouldFetchUserReactions && fetchedUserReactions) {
      setUserReactions(fetchedUserReactions);
    }
  }, [shouldFetchUserReactions, fetchedUserReactions]);
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        // Ensure we pass the engagement data in the correct format
        const summaryToPass = engagementSummary
          ? 'engagement' in engagementSummary
            ? engagementSummary
            : { engagement: engagementSummary }
          : undefined;

        // Define callback to update local state when reactions change
        const handleReactionChange = (
          reactionType: string,
          action: 'add' | 'remove',
          updatedUserReactions: string[],
          updatedEngagement?: any
        ) => {
          // Update local state with new data
          setUserReactions(updatedUserReactions);
          if (updatedEngagement) {
            setEngagementSummary(updatedEngagement);
          }
        };

        openDialog(
          objectId,
          objectType,
          title,
          details,
          userReactions,
          summaryToPass,
          handleReactionChange
        );
      }}
      className={clsx(
        'backdrop-blur-sm rounded-full shadow-lg flex items-center gap-1.5 transition-all duration-200',
        'hover:bg-primary-500 hover:text-white hover:scale-105',
        isCompact ? 'px-2 py-1' : 'px-3 py-1.5',
        hasUserReaction ? 'ring-2 ring-primary-300 bg-green-100' : 'bg-white/95',
        className
      )}
      aria-label="React"
    >
      {hasUserReaction ? (
        <span className={clsx('leading-none', isCompact ? 'text-sm' : 'text-base')}>
          {userReactionEmoji}
        </span>
      ) : (
        <svg
          className={clsx(isCompact ? 'w-3.5 h-3.5' : 'w-4 h-4')}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      )}
      {totalReactions > 0 && (
        <span className={clsx('font-medium', isCompact ? 'text-xs' : 'text-sm')}>
          {totalReactions}
        </span>
      )}
    </button>
  );
}
